/* eslint-disable react/no-danger */
import React, { memo } from 'react';
import type { FC } from 'react';
import { useLocation } from 'react-router';
import type { PageFC } from '@salesforce/pwa-kit-react-sdk';
import type { Response } from 'express';
import { Layout } from '../../components/layout/Layout';
import Seo from '../../components/seo';
import { isNoIndexPage } from '../../utils/is-no-index-page';
import { STORES_PAGE_PATTERN, getRioSeoData } from '../../utils/rio-utils';
import { isServer } from '../../utils/utils';
import { configHtmlCache } from '../utils/config-html-cache';
import styles from './Stores.module.scss';
import type { RioData } from '../../utils/rio-utils';

function StoreContentFn({ content }: Pick<RioData, 'content'>): ReturnType<FC> {
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
}

const StoreContent = memo(StoreContentFn);

type Props = {
    rioData?: RioData;
};

const Stores: PageFC<Props> = ({ rioData }: Props) => {
    const { title, metaTags, linkTags, headContent = '', content = '' } = rioData ?? {};

    const location = useLocation();
    const shouldNoIndex = isNoIndexPage(location?.pathname);

    return (
        <>
            <Seo title={title} noIndex={shouldNoIndex}>
                {metaTags?.map((tag) => (
                    <meta key={JSON.stringify(tag)} {...tag} />
                ))}
                {linkTags?.map((tag) => (
                    <link key={JSON.stringify(tag)} {...tag} />
                ))}
            </Seo>
            <Layout>
                <div className={styles.Isolating}>
                    <div dangerouslySetInnerHTML={{ __html: headContent }} />
                    <StoreContent content={content} />
                </div>
            </Layout>
        </>
    );
};

Stores.defaultProps = {
    rioData: undefined,
};

Stores.shouldGetProps = (): Promise<boolean> => Promise.resolve(isServer);

Stores.getProps = async ({
    res,
    location,
}: {
    location: Location;
    res: Partial<Response>;
}): Promise<Props> => {
    try {
        configHtmlCache(res);
        const path = location?.pathname ?? '';
        const rioData = await getRioSeoData(path.replace(STORES_PAGE_PATTERN, '$1'));

        return {
            rioData,
        };
    } catch (e) {
        console.error(e);
        return {};
    }
};

export { Stores };
